import { create } from 'zustand';
import { clearStoredToken, getStoredToken } from './tokenStore';

/**
 * Auth state for the admin dashboard.
 *
 * The bearer token sits in `localStorage` (managed by `tokenStore.ts`).
 * `apiFetch` reads it on every request. This store only mirrors the
 * *fact* that we are signed in plus the active admin user id, so the
 * route guard and re-auth modal can react to it.
 *
 * Validates Requirements 20.1, 20.2, 20.4.
 */
export interface AuthState {
  isAuthenticated: boolean;
  userId: string | null;
  /** Optional unix-seconds expiry. Used only for UI hints. */
  expiresAt: number | null;
  setAuth(userId: string, expiresAt?: number): void;
  clearAuth(): void;
  signOut(): Promise<void>;
  bootstrapAuth(): Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  userId: null,
  expiresAt: null,
  setAuth: (userId, expiresAt) =>
    set({
      isAuthenticated: true,
      userId,
      expiresAt: expiresAt ?? null,
    }),
  clearAuth: () => {
    clearStoredToken();
    set({ isAuthenticated: false, userId: null, expiresAt: null });
  },
  signOut: async () => {
    clearStoredToken();
    set({ isAuthenticated: false, userId: null, expiresAt: null });
  },
  bootstrapAuth: async () => {
    const token = getStoredToken();
    if (token) {
      set({ isAuthenticated: true, userId: 'unknown-user', expiresAt: null });
    } else {
      set({ isAuthenticated: false, userId: null, expiresAt: null });
    }
  },
}));
